/**
 * home.js — the landing page.
 *
 * Three things changed relative to the old renderer:
 *
 *   1. Nothing is hardcoded. The hero copy, the feature tiles and the section
 *      headings all come from /api/home/. Previously the hero headline, the
 *      two "Products / Training" tiles and their body copy were literal
 *      strings in the bundle, so editing them meant a redeploy.
 *   2. Companies render as a slider with the same fixed navigation the
 *      certificates use, rather than a static grid.
 *   3. The CRM banner is gone — the product does not exist.
 */

import {esc, img, icon, truncate} from '../lib/dom.js';
import {gettext, getLang, num, formatDate, localePath} from '../lib/i18n.js';
import {sliderMarkup} from '../ui/slider.js';

/* ------------------------------------------------------------------ *
 * Sections
 * ------------------------------------------------------------------ */

function renderHero(hero = {}) {
    const bg = hero.background || '/static/images/hero-bg.jpg';
    // The effect, its background and its particle shape are all dashboard
    // settings; ui/particles.js picks the config up from this attribute.
    const particles = hero.particles
        ? ` data-hero-particles='${esc(JSON.stringify(hero.particles))}'`
        : '';

    return `
    <section class="nv-hero"${particles}>
      <img src="${esc(bg)}" alt="" class="nv-hero__bg"
           onerror="this.style.display='none'">
      <div class="nv-container nv-hero__inner">
        <div>
          ${hero.eyebrow ? `<span class="nv-hero__eyebrow">${esc(hero.eyebrow)}</span>` : ''}
          <h1 class="nv-hero__title">${hero.title ? esc(hero.title) : ''}</h1>
        </div>
        ${hero.subtitle || hero.text ? `
          <div class="nv-hero__aside">
            ${hero.subtitle ? `<h2 class="nv-hero__subtitle">${esc(hero.subtitle)}</h2>` : ''}
            ${hero.text ? `<p class="nv-hero__text">${esc(hero.text)}</p>` : ''}
            ${hero.cta_link ? `
              <div class="nv-hero__actions">
                <a href="${esc(localePath(hero.cta_link))}" class="nv-btn nv-btn--accent nv-btn--lg">
                  <span>${esc(hero.cta_label || gettext('Learn more'))}</span>
                  ${icon('arrow-right', {className: 'nv-icon-flip'})}
                </a>
              </div>` : ''}
          </div>` : ''}
      </div>
    </section>`;
}

function certificateSlide(cert) {
    return `
    <article class="nv-cert-card">
      <div class="nv-cert-card__media" data-lightbox="certificates"
           data-lightbox-src="${esc(cert.image || '')}"
           data-lightbox-caption="${esc(cert.title || '')}">
        ${img(cert.image, cert.title)}
      </div>
      <div class="nv-cert-card__body">
        ${cert.date ? `<span class="nv-cert-card__date nv-num">${num(cert.date)}</span>` : ''}
        <h3 class="nv-cert-card__title">${esc(cert.title)}</h3>
        ${cert.description
        ? `<p class="nv-cert-card__text">${esc(truncate(cert.description, 18))}</p>` : ''}
        ${cert.link ? `
          <div class="nv-cert-card__link">
            <a href="${esc(cert.link)}" target="_blank" rel="noopener" class="nv-link-action">
              <span>${gettext('View')}</span>
              ${icon('arrow-right', {className: 'nv-icon-flip'})}
            </a>
          </div>` : ''}
      </div>
    </article>`;
}

function renderCertificates(certificates, heading) {
    if (!certificates || !certificates.length) return '';

    return `
    <section class="nv-section">
      <div class="nv-container">
        <div class="nv-section-head">
          <div class="nv-section-head__text">
            <span class="nv-section-eyebrow">
              ${icon('award')} <span>${gettext('Credentials')}</span>
            </span>
            <h2 class="nv-section-title">${esc(heading || gettext('Certificates'))}</h2>
          </div>
        </div>
        ${sliderMarkup(certificates.map(certificateSlide), {
        id: 'cert-slider',
        autoplay: true,
        label: gettext('Certificates'),
    })}
      </div>
    </section>`;
}

function companySlide(company) {
    const inner = `
    <div class="nv-company-card__logo">
      ${company.image ? img(company.image, company.title) : icon('building')}
    </div>
    <h3 class="nv-company-card__title">${esc(company.title)}</h3>
    ${company.description
        ? `<p class="nv-company-card__text">${esc(truncate(company.description, 14))}</p>` : ''}`;

    return company.link
        ? `<a href="${esc(company.link)}" target="_blank" rel="noopener" class="nv-company-card">${inner}</a>`
        : `<div class="nv-company-card">${inner}</div>`;
}

/** Companies now use the same slider component as the certificates. */
function renderCompanies(companies, heading) {
    if (!companies || !companies.length) return '';

    return `
    <section class="nv-section nv-section--alt">
      <div class="nv-container">
        <div class="nv-section-head">
          <div class="nv-section-head__text">
            <span class="nv-section-eyebrow">
              ${icon('handshake')} <span>${gettext('Partnership')}</span>
            </span>
            <h2 class="nv-section-title">
              ${esc(heading || gettext('Our Clients & Partners'))}
            </h2>
          </div>
        </div>
        ${sliderMarkup(companies.map(companySlide), {
        id: 'company-slider',
        variant: 'nv-slider--narrow',
        autoplay: true,
        label: gettext('Our partners'),
    })}
      </div>
    </section>`;
}

function newsCard(item) {
    return `
    <a href="${esc(localePath(`/news/${item.slug}`))}" class="nv-card">
      <div class="nv-card__media">
        ${item.cover_image
        ? img(item.cover_image, item.title)
        : `<span class="nv-card__placeholder">${icon('newspaper')}</span>`}
      </div>
      <div class="nv-card__body">
        <div class="nv-card__meta">
          ${icon('calendar-day', {solid: false})}
          <span class="nv-num">${esc(formatDate(item.published_at))}</span>
        </div>
        <h3 class="nv-card__title">${esc(item.title)}</h3>
        <p class="nv-card__text">${esc(truncate(item.description, 20))}</p>
        <div class="nv-card__footer">
          <span class="nv-link-action">
            <span>${gettext('Read more')}</span>
            ${icon('arrow-right', {className: 'nv-icon-flip'})}
          </span>
        </div>
      </div>
    </a>`;
}

function renderNews(news, heading) {
    if (!news || !news.length) return '';

    return `
    <section class="nv-section">
      <div class="nv-container">
        <div class="nv-section-head">
          <div class="nv-section-head__text">
            <span class="nv-section-eyebrow">
              ${icon('bullhorn')} <span>${gettext('Latest')}</span>
            </span>
            <h2 class="nv-section-title">${esc(heading || gettext('News & Events'))}</h2>
          </div>
          <div class="nv-section-head__aside">
            <a href="${esc(localePath('/news/'))}" class="nv-btn nv-btn--outline nv-btn--sm">
              <span>${gettext('All news')}</span>
              ${icon('arrow-right', {className: 'nv-icon-flip'})}
            </a>
          </div>
        </div>
        <div class="nv-grid nv-grid--3">${news.map(newsCard).join('')}</div>
      </div>
    </section>`;
}

/** Lab tiles. The floating hero elements are decorative and come from the DB. */
function labCard(lab, index) {
    const gradients = [
        'linear-gradient(135deg, #06182c 0%, #12365f 45%, #24609f 100%)',
        'linear-gradient(135deg, #101a30 0%, #1b3a5c 45%, #2b6f8f 100%)',
        'linear-gradient(135deg, #0d1f22 0%, #17423a 45%, #1f7a4d 100%)',
        'linear-gradient(135deg, #1e1428 0%, #43214a 45%, #8f2d1c 100%)',
        'linear-gradient(135deg, #1c1608 0%, #4a3312 45%, #a86a06 100%)',
        'linear-gradient(135deg, #0b1836 0%, #23306b 45%, #4a52b0 100%)',
    ];

        const overlayGradients = [
        'linear-gradient(135deg, rgba(6, 24, 44, 0.7) 0%, rgba(18, 54, 95, 0.8) 45%, rgba(36, 96, 159, 0.9) 100%)',
        'linear-gradient(135deg, rgba(16, 26, 48, 0.7) 0%, rgba(27, 58, 92, 0.8) 45%, rgba(43, 111, 143, 0.9) 100%)',
        'linear-gradient(135deg, rgba(13, 31, 34, 0.7) 0%, rgba(23, 66, 58, 0.8) 45%, rgba(31, 122, 77, 0.9) 100%)',
        'linear-gradient(135deg, rgba(30, 20, 40, 0.7) 0%, rgba(67, 33, 74, 0.8) 45%, rgba(143, 45, 28, 0.9) 100%)',
        'linear-gradient(135deg, rgba(28, 22, 8, 0.7) 0%, rgba(74, 51, 18, 0.8) 45%, rgba(168, 106, 6, 0.9) 100%)',
        'linear-gradient(135deg, rgba(11, 24, 54, 0.7) 0%, rgba(35, 48, 107, 0.8) 45%, rgba(74, 82, 176, 0.9) 100%)',
    ];

    const bgStyle = lab.image
        ? `background-image: ${overlayGradients[index % overlayGradients.length]}, url('${esc(lab.image)}'); background-position: center; background-size: cover; background-repeat: no-repeat;`
        : `background: ${gradients[index % gradients.length]};`;

    return `
    <a href="${esc(localePath(`/lab/${lab.slug}`))}" class="nv-lab-card"
       style="${bgStyle}">
      <div class="nv-lab-card__content"> 
            ${getLang() === 'fa' && lab.title_en ? `<span class="nv-lab-card__en">${esc(lab.title_en)}</span>` : ''}
        <h3 class="nv-lab-card__title">${esc(lab.title)}</h3>
        <p class="nv-lab-card__text">${esc(truncate(lab.description, 18))}</p>
        <span class="nv-lab-card__cta">
          <span>${gettext('Explore lab')}</span>
          ${icon('arrow-right', {className: 'nv-icon-flip'})}
        </span>
      </div>
    </a>`;
}

function renderLabs(labs, heading) {
    if (!labs || !labs.length) return '';

    return `
    <section class="nv-section nv-section--alt">
      <div class="nv-container">
        <div class="nv-section-head">
          <div class="nv-section-head__text">
            <span class="nv-section-eyebrow">
              ${icon('flask')} <span>${gettext('Research')}</span>
            </span>
            <h2 class="nv-section-title">${esc(heading || gettext('Research Labs'))}</h2>
          </div>
        </div>
        <div class="nv-grid nv-grid--3">
          ${labs.map(labCard).join('')}
        </div>
      </div>
    </section>`;
}

/** Feature tiles, now driven by the `highlights` array from the API. */
function renderHighlights(highlights, heading) {
    if (!highlights || !highlights.length) return '';

    return `
    <section class="nv-section">
      <div class="nv-container">
        ${heading ? `
        <div class="nv-section-head">
          <div class="nv-section-head__text">
            <h2 class="nv-section-title">${esc(heading)}</h2>
          </div>
        </div>` : ''}
        <div class="nv-grid nv-grid--2">
          ${highlights.map((h) => `
            <a href="${esc(localePath(h.link || '/'))}" class="nv-feature-card">
              ${h.image ? `<img src="${esc(h.image)}" alt="" class="nv-feature-card__bg">` : ''}
              ${h.eyebrow ? `<span class="nv-feature-card__eyebrow">${esc(h.eyebrow)}</span>` : ''}
              <h3 class="nv-feature-card__title">${esc(h.title)}</h3>
              ${h.text ? `<p class="nv-feature-card__text">${esc(h.text)}</p>` : ''}
              <span class="nv-feature-card__cta">
                <span>${esc(h.cta_label || gettext('View'))}</span>
                ${icon('arrow-right', {className: 'nv-icon-flip'})}
              </span>
            </a>`).join('')}
        </div>
      </div>
    </section>`;
}

/* ------------------------------------------------------------------ *
 * Entry
 * ------------------------------------------------------------------ */

/**
 * Which renderer draws each section key. The order the page ends up in comes
 * from `data.sections`, so an editor can reshuffle the landing page without a
 * redeploy; this map only says how to draw a block, never where it goes.
 */
const SECTION_RENDERERS = {
    hero: (data, section) => renderHero(data.hero || {}),
    certificates: (data, section) => renderCertificates(data.certificates, section && section.heading),
    companies: (data, section) => renderCompanies(data.companies, (section && section.heading) || data.companies_heading),
    news: (data, section) => renderNews(data.news, section && section.heading),
    labs: (data, section) => renderLabs(data.labs, section && section.heading),
    highlights: (data, section) => renderHighlights(data.highlights, section && section.heading),
};

const DEFAULT_SECTION_ORDER = ['hero', 'certificates', 'companies', 'news', 'labs', 'highlights'];

export function renderHome(data) {
    // Accommodate both the default layout and the new dynamic `content_source` layout
    const sections = data.sections && data.sections.length
        ? data.sections
        : DEFAULT_SECTION_ORDER.map(k => ({key: k, content_source: k}));

    return sections
        .filter(section => section && section.is_active !== false)
        .map((section) => {
            const type = section.content_source || section.key;
            return SECTION_RENDERERS[type] ? SECTION_RENDERERS[type](data, section) : '';
        })
        .join('');
}